import { useEffect } from 'react';
import { App, Form, Input, Modal, Switch } from 'antd';
import { useCreateApp, useUpdateApp } from '@/hooks/useApps';
import type { AppDto } from '@/types/app';

interface Props {
  open: boolean;
  app: AppDto | null;
  onClose: () => void;
}

interface FormValues {
  packageName: string;
  title: string;
  iconUrl?: string;
  description?: string;
  category?: string;
  isApprovedInPlay: boolean;
}

const COMMON_CATEGORIES = [
  'Productividad',
  'Comunicacion',
  'Mensajeria',
  'Negocios',
  'Herramientas',
  'Mapas y navegacion',
  'Sistema',
];

export function AppFormModal({ open, app, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const isEdit = app != null;

  const createMut = useCreateApp();
  const updateMut = useUpdateApp();

  useEffect(() => {
    if (open) {
      if (app) {
        form.setFieldsValue({
          packageName: app.packageName,
          title: app.title,
          iconUrl: app.iconUrl ?? '',
          description: app.description ?? '',
          category: app.category ?? '',
          isApprovedInPlay: app.isApprovedInPlay,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ isApprovedInPlay: false });
      }
    }
  }, [open, app, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isEdit && app) {
        await updateMut.mutateAsync({
          id: app.id,
          request: {
            title: values.title.trim(),
            iconUrl: values.iconUrl?.trim() || undefined,
            description: values.description?.trim() || undefined,
            category: values.category?.trim() || undefined,
          },
        });
        message.success('App actualizada.');
      } else {
        await createMut.mutateAsync({
          packageName: values.packageName.trim().toLowerCase(),
          title: values.title.trim(),
          iconUrl: values.iconUrl?.trim() || undefined,
          description: values.description?.trim() || undefined,
          category: values.category?.trim() || undefined,
          isApprovedInPlay: values.isApprovedInPlay,
        });
        message.success('App creada.');
      }

      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  return (
    <Modal
      title={isEdit ? `Editar app: ${app?.title}` : 'Nueva app'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? 'Guardar cambios' : 'Crear'}
      cancelText="Cancelar"
      confirmLoading={createMut.isPending || updateMut.isPending}
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Package name"
          name="packageName"
          rules={[
            { required: true, message: 'Package name requerido.' },
            { pattern: /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i, message: 'Package name invalido (ej: com.empresa.app).' },
          ]}
          extra="Identificador unico de la app en Google Play (ej: com.whatsapp)"
        >
          <Input placeholder="com.empresa.app" disabled={isEdit} />
        </Form.Item>

        <Form.Item
          label="Titulo"
          name="title"
          rules={[{ required: true, message: 'Titulo requerido.' }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item label="Categoria" name="category">
          <Input
            list="app-categories"
            placeholder="Productividad, Comunicacion, etc."
            maxLength={100}
          />
        </Form.Item>
        <datalist id="app-categories">
          {COMMON_CATEGORIES.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        <Form.Item label="URL del icono" name="iconUrl">
          <Input placeholder="https://..." maxLength={500} />
        </Form.Item>

        <Form.Item label="Descripcion" name="description">
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            label="Aprobada en Managed Google Play"
            name="isApprovedInPlay"
            valuePropName="checked"
            extra="Solo apps aprobadas pueden asignarse a perfiles."
          >
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
